import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { getBookingStatus, getReportParties } from "../../api/airwallex";
import {
  getDisputes,
  getOpenDisputeIds,
  findOpenDisputeByRoomCode,
  downloadDisputesCsv,
} from "../../api/disputes";
import BalanceCard from "../../components/wallet/BalanceCard";
import AccountsTable from "../../components/wallet/AccountsTable";
import SummaryCards from "../../components/wallet/SummaryCards";
import BookingsTableFilters from "../../components/wallet/BookingsTableFilters";
import BookingsTable from "../../components/wallet/BookingsTable";
import DisputesTable from "../../components/wallet/DisputesTable";
import DisputesTableFilters from "../../components/wallet/DisputesTableFilters";
import DisputeFormModal from "../../components/wallet/DisputeFormModal";
import DisputeDetailModal from "../../components/wallet/DisputeDetailModal";
import Pagination from "../../components/wallet/Pagination";
import { buildBookingsReport } from "../../utils/walletReport";
import { toast } from "react-toastify";
import "./WalletDashboard.css";

// Display labels for the raw filter values sent to the backend, used for the
// "Report Filters" section of the downloaded report.
const BOOKING_TYPE_LABELS = {
  REFUNDABLE: "Refundable",
  "NON-REFUNDABLE": "Non-Refundable",
};
const TRANSACTION_TYPE_LABELS = { PAYMENT: "Payment", REFUND: "Refund" };
const TRANSACTION_STATUS_LABELS = {
  PAID: "Paid",
  OVERDUE: "Overdue",
  REFUND: "Refunded",
};

const capitalize = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value;

// Filter date bounds are already YYYY-MM-DD strings; render them as an en-dash
// range to match the sample report (e.g. "2026-01-01 – 2026-01-31").
const formatFilterDateRange = (gt, lt) => {
  if (gt && lt) return `${gt} – ${lt}`;
  if (gt) return `From ${gt}`;
  if (lt) return `Until ${lt}`;
  return "";
};

// Shape the ht-admin report-parties "to" block into the report's recipient
// object. Returns null when there are no usable details so the "To" section is
// skipped (per IO-142).
const mapRecipient = (to) => {
  if (!to || !to.name) return null;
  return { name: to.name, address: to.address, taxId: to.taxId };
};

// Fetch the invoice recipient ("To") for the report via ht-admin. Best-effort:
// any failure just omits the section rather than blocking the download.
const fetchReportRecipient = async (currency) => {
  try {
    const response = await getReportParties(currency);
    return mapRecipient(response?.data?.to);
  } catch (error) {
    if (error?.message !== "SESSION_EXPIRED") {
      console.error("Unable to load report recipient details:", error);
    }
    return null;
  }
};

// Build the list of filters the user actually applied (skips unset ones) so the
// report only lists active filters, per IO-142. Order mirrors the sample report.
const buildAppliedFilters = (searchQuery, filters) => {
  const applied = [];
  if (searchQuery) applied.push({ label: "Search", value: searchQuery });
  if (filters.bookingStatus)
    applied.push({
      label: "Booking Status",
      value: capitalize(filters.bookingStatus),
    });
  if (filters.bookingType)
    applied.push({
      label: "Booking Type",
      value: BOOKING_TYPE_LABELS[filters.bookingType] || filters.bookingType,
    });
  const checkInRange = formatFilterDateRange(
    filters.checkInDateGt,
    filters.checkInDateLt
  );
  if (checkInRange)
    applied.push({ label: "Check-in Date Range", value: checkInRange });
  const transactionRange = formatFilterDateRange(
    filters.paymentDateGt,
    filters.paymentDateLt
  );
  if (transactionRange)
    applied.push({ label: "Transaction Date Range", value: transactionRange });
  if (filters.transactionType)
    applied.push({
      label: "Transaction Type",
      value:
        TRANSACTION_TYPE_LABELS[filters.transactionType] ||
        filters.transactionType,
    });
  if (filters.transactionStatus)
    applied.push({
      label: "Transaction Status",
      value:
        TRANSACTION_STATUS_LABELS[filters.transactionStatus] ||
        filters.transactionStatus,
    });
  return applied;
};

const COLUMN_RENAMES = {
  payment_amount: "transaction_amount",
  Payment_Amount: "Transaction_Amount",
  "Payment Amount": "Transaction Amount",
  payment_status: "transaction_status",
  Payment_Status: "Transaction_Status",
  "Payment Status": "Transaction Status",
  payment_date: "transaction_date",
  Payment_Date: "Transaction_Date",
  "Payment Date": "Transaction Date",
};

const COLUMNS_TO_REMOVE = [
  "cxl_amount",
  "Cxl_Amount",
  "CXL Amount",
  "credit_amount",
  "Credit_Amount",
  "Credit Amount",
  "credit_date",
  "Credit_Date",
  "Credit Date",
];

const transformExportCsv = (csvText) => {
  const lines = csvText.split("\n");
  if (lines.length === 0) return csvText;

  // Parse header to find column indices
  const headers = lines[0].split(",");
  const removeIndices = new Set();
  let paymentStatusIdx = -1;
  let paymentAmountIdx = -1;

  headers.forEach((header, idx) => {
    const trimmed = header.trim().replace(/^"|"$/g, "");
    if (
      COLUMNS_TO_REMOVE.some(
        (col) => col.toLowerCase() === trimmed.toLowerCase()
      )
    ) {
      removeIndices.add(idx);
    }
    if (trimmed.toLowerCase().replace(/[\s_]/g, "") === "paymentstatus") {
      paymentStatusIdx = idx;
    }
    if (trimmed.toLowerCase().replace(/[\s_]/g, "") === "paymentamount") {
      paymentAmountIdx = idx;
    }
  });

  // Rename headers
  const renamedHeaders = headers.map((header) => {
    const trimmed = header.trim().replace(/^"|"$/g, "");
    for (const [from, to] of Object.entries(COLUMN_RENAMES)) {
      if (trimmed.toLowerCase() === from.toLowerCase()) {
        // Preserve original quoting
        return header.startsWith('"') ? `"${to}"` : to;
      }
    }
    return header;
  });

  // Process all lines
  return lines
    .map((line, lineIdx) => {
      if (line.trim() === "") return line;
      const cols = line.split(",");

      // Transform data rows
      if (lineIdx > 0) {
        // Show "REFUNDED" instead of "REFUND"
        if (paymentStatusIdx >= 0 && cols[paymentStatusIdx]) {
          const val = cols[paymentStatusIdx].trim().replace(/^"|"$/g, "");
          if (val.toUpperCase() === "REFUND") {
            cols[paymentStatusIdx] = cols[paymentStatusIdx].startsWith('"')
              ? '"REFUNDED"'
              : "REFUNDED";
          }
        }
        // Negate payment_amount for refunds
        if (
          paymentStatusIdx >= 0 &&
          paymentAmountIdx >= 0 &&
          cols[paymentStatusIdx]
        ) {
          const status = cols[paymentStatusIdx].trim().replace(/^"|"$/g, "");
          if (
            status.toUpperCase() === "REFUND" ||
            status.toUpperCase() === "REFUNDED"
          ) {
            const amountVal = cols[paymentAmountIdx]
              .trim()
              .replace(/^"|"$/g, "");
            const num = parseFloat(amountVal);
            if (!isNaN(num) && num > 0) {
              cols[paymentAmountIdx] = cols[paymentAmountIdx].startsWith('"')
                ? `"${-num}"`
                : String(-num);
            }
          }
        }
      } else {
        // Use renamed headers for first line
        return renamedHeaders
          .filter((_, idx) => !removeIndices.has(idx))
          .join(",");
      }

      return cols.filter((_, idx) => !removeIndices.has(idx)).join(",");
    })
    .join("\n");
};

const WalletDashboard = ({ balances = [], onRefreshData }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAccountIndex, setSelectedAccountIndex] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [bookingsError, setBookingsError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filter state
  const [filters, setFilters] = useState({
    bookingStatus: "",
    bookingType: "",
    transactionType: "",
    transactionStatus: "",
    checkInDateGt: null,
    checkInDateLt: null,
    paymentDateGt: null,
    paymentDateLt: null,
    creditDateGt: null,
    creditDateLt: null,
  });

  // Disputes view state
  const [view, setView] = useState("transactions"); // "transactions" | "disputes"
  const [openDisputeIds, setOpenDisputeIds] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [disputesTotal, setDisputesTotal] = useState(0);
  const [disputesTotalPages, setDisputesTotalPages] = useState(1);
  const [disputesPage, setDisputesPage] = useState(1);
  const [disputeSearchQuery, setDisputeSearchQuery] = useState("");
  const [debouncedDisputeSearch, setDebouncedDisputeSearch] = useState("");
  const [disputeStatus, setDisputeStatus] = useState("");
  const [disputeDateRange, setDisputeDateRange] = useState({
    submittedAtGt: null,
    submittedAtLt: null,
  });
  const [disputeAggregates, setDisputeAggregates] = useState({
    disputedBookingAmount: 0,
    feeImpact: 0,
  });
  const [loadingDisputes, setLoadingDisputes] = useState(false);

  // Dispute modals
  const [disputeFormBooking, setDisputeFormBooking] = useState(null);
  const [reviewDisputeId, setReviewDisputeId] = useState(null);

  // Calculate total balance from sum of all usd_total_amount
  const calculateTotalBalance = () => {
    if (!balances || balances.length === 0) {
      return "$0.00";
    }
    const total = balances.reduce((sum, balance) => {
      const amount = balance.usd_total_amount || 0;
      return (
        sum + (typeof amount === "number" ? amount : parseFloat(amount) || 0)
      );
    }, 0);
    return `$${total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const totalBalance = calculateTotalBalance();

  // Debounced search query
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Debounced dispute search — kept separate from the bookings search so the
  // two views don't share a query box.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedDisputeSearch(disputeSearchQuery);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [disputeSearchQuery]);

  // Update fetchBookings to use debouncedSearchQuery
  const fetchBookings = useCallback(async () => {
    if (!selectedAccount) return;

    setLoadingBookings(true);
    setBookingsError(null);

    try {
      const params = {
        currency: selectedAccount.currency,
        page: currentPage,
        size: pageSize,
      };

      // Add debounced search query if present
      if (debouncedSearchQuery) {
        params.query = debouncedSearchQuery;
      }

      // Add filter parameters
      if (filters.bookingStatus) {
        params.bookingStatus = filters.bookingStatus;
      }
      if (filters.bookingType) {
        params.bookingType = filters.bookingType;
      }
      if (filters.transactionType) {
        params.transactionType = filters.transactionType;
      }
      if (filters.transactionStatus) {
        params.transactionStatus = filters.transactionStatus;
      }
      if (filters.checkInDateGt) {
        params.checkInDateGt = filters.checkInDateGt;
      }
      if (filters.checkInDateLt) {
        params.checkInDateLt = filters.checkInDateLt;
      }
      if (filters.paymentDateGt) {
        params.paymentDateGt = filters.paymentDateGt;
      }
      if (filters.paymentDateLt) {
        params.paymentDateLt = filters.paymentDateLt;
      }
      if (filters.creditDateGt) {
        params.creditDateGt = filters.creditDateGt;
      }
      if (filters.creditDateLt) {
        params.creditDateLt = filters.creditDateLt;
      }

      const response = await getBookingStatus(params);

      if (response && response.status === 200 && response.data) {
        const bookingsData = Array.isArray(response.data)
          ? response.data
          : response.data.data || response.data.content || [];
        setBookings(bookingsData);

        // Update total and total pages if pagination info is available
        if (response.data.total !== undefined) {
          setTotal(response.data.total);
          if (response.data.totalPages) {
            setTotalPages(response.data.totalPages);
          } else {
            setTotalPages(Math.ceil(response.data.total / pageSize));
          }
        } else {
          setTotal(0);
          setTotalPages(1);
        }
      } else {
        setBookings([]);
        setTotal(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      setBookingsError(error.message || "Failed to load bookings");
      setBookings([]);
    } finally {
      setLoadingBookings(false);
      setIsInitialLoad(false);
    }
  }, [selectedAccount, currentPage, pageSize, debouncedSearchQuery, filters]);

  // Fetch bookings when filters, page, or debounced search changes
  useEffect(() => {
    if (selectedAccount) {
      fetchBookings();
    }
  }, [selectedAccount, currentPage, fetchBookings]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleDownload = async () => {
    if (!selectedAccount) return;

    try {
      const params = {
        currency: selectedAccount.currency,
        export: true,
      };

      // Add all current filters
      if (searchQuery) params.query = searchQuery;
      if (filters.bookingStatus) params.bookingStatus = filters.bookingStatus;
      if (filters.bookingType) params.bookingType = filters.bookingType;
      if (filters.transactionType)
        params.transactionType = filters.transactionType;
      if (filters.transactionStatus)
        params.transactionStatus = filters.transactionStatus;
      if (filters.checkInDateGt) params.checkInDateGt = filters.checkInDateGt;
      if (filters.checkInDateLt) params.checkInDateLt = filters.checkInDateLt;
      if (filters.paymentDateGt) params.paymentDateGt = filters.paymentDateGt;
      if (filters.paymentDateLt) params.paymentDateLt = filters.paymentDateLt;
      if (filters.creditDateGt) params.creditDateGt = filters.creditDateGt;
      if (filters.creditDateLt) params.creditDateLt = filters.creditDateLt;

      const response = await getBookingStatus(params);

      // Handle blob response for CSV file download
      if (response && response.data) {
        // Transform CSV to match UI changes
        const csvText =
          typeof response.data === "string"
            ? response.data
            : await response.data.text();
        const transformedCsv = transformExportCsv(csvText);

        // Recipient ("To") details from the invoice schedule via ht-admin —
        // best-effort, so a failure just omits the section without blocking the
        // download.
        const recipient = await fetchReportRecipient(selectedAccount.currency);

        // Build a branded XLSX report (company details, tax ID and seal header
        // plus the bookings table) so client finance teams can reconcile. The
        // report also carries the recipient, total, applied filters, and a
        // generated-on timestamp for compliance (IO-142).
        const blob = await buildBookingsReport(transformedCsv, {
          currency: selectedAccount.currency,
          recipient,
          appliedFilters: buildAppliedFilters(searchQuery, filters),
          generatedOn: new Date(),
        });

        // Create a temporary URL for the blob
        const url = window.URL.createObjectURL(blob);

        // Create a temporary anchor element to trigger download
        const link = document.createElement("a");
        link.href = url;

        // Generate filename with currency and timestamp
        const timestamp = new Date().toISOString().split("T")[0];
        const filename = `bookings_${selectedAccount.currency}_${timestamp}.xlsx`;
        link.download = filename;

        // Append to body, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the URL object
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error downloading bookings:", error);
    }
  };

  const handleDateRangeApply = (filterType, startDate, endDate) => {
    setFilters((prev) => {
      const newFilters = { ...prev };

      if (filterType === "checkIn") {
        newFilters.checkInDateGt = startDate || null;
        newFilters.checkInDateLt = endDate || null;
      } else if (filterType === "payment") {
        newFilters.paymentDateGt = startDate || null;
        newFilters.paymentDateLt = endDate || null;
      } else if (filterType === "credit") {
        newFilters.creditDateGt = startDate || null;
        newFilters.creditDateLt = endDate || null;
      }

      return newFilters;
    });
    setCurrentPage(1);
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleAccountSelect = (account, index) => {
    setSelectedAccount(account);
    setSelectedAccountIndex(index);
    setCurrentPage(1);
    setIsInitialLoad(true);
    // Reset filters when account changes
    setFilters({
      bookingStatus: "",
      bookingType: "",
      transactionType: "",
      transactionStatus: "",
      checkInDateGt: null,
      checkInDateLt: null,
      paymentDateGt: null,
      paymentDateLt: null,
      creditDateGt: null,
      creditDateLt: null,
    });
    setSearchQuery("");
  };

  const handleThresholdUpdate = () => {
    // Refresh data after threshold update
    if (onRefreshData) {
      onRefreshData();
    }
  };

  // ---------- Disputes ----------

  // Open-dispute confirmation IDs gate the entry-point Dispute button.
  const fetchOpenDisputeIds = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      const response = await getOpenDisputeIds({
        currency: selectedAccount.currency,
      });
      setOpenDisputeIds(response?.data?.htConfirmationIds || []);
    } catch (error) {
      if (error?.message !== "SESSION_EXPIRED") {
        console.error("Error fetching open disputes:", error);
      }
    }
  }, [selectedAccount]);

  const fetchDisputes = useCallback(async () => {
    if (!selectedAccount) return;
    setLoadingDisputes(true);
    try {
      const response = await getDisputes({
        currency: selectedAccount.currency,
        query: debouncedDisputeSearch || undefined,
        status: disputeStatus || undefined,
        submittedAtGt: disputeDateRange.submittedAtGt || undefined,
        submittedAtLt: disputeDateRange.submittedAtLt || undefined,
        page: disputesPage,
        size: pageSize,
      });
      if (response && response.status === 200 && response.data) {
        const data = response.data;
        setDisputes(Array.isArray(data.data) ? data.data : []);
        setDisputesTotal(data.total || 0);
        setDisputesTotalPages(
          data.totalPages || Math.ceil((data.total || 0) / pageSize) || 1
        );
        setDisputeAggregates(
          data.aggregates || { disputedBookingAmount: 0, feeImpact: 0 }
        );
      } else {
        setDisputes([]);
        setDisputesTotal(0);
        setDisputesTotalPages(1);
      }
    } catch (error) {
      if (error?.message !== "SESSION_EXPIRED") {
        console.error("Error fetching disputes:", error);
      }
      setDisputes([]);
    } finally {
      setLoadingDisputes(false);
    }
  }, [
    selectedAccount,
    disputesPage,
    pageSize,
    debouncedDisputeSearch,
    disputeStatus,
    disputeDateRange,
  ]);

  // Refresh open-dispute IDs whenever an account is (re)selected.
  useEffect(() => {
    if (selectedAccount) {
      fetchOpenDisputeIds();
    }
  }, [selectedAccount, fetchOpenDisputeIds]);

  // Load disputes (list + aggregates) whenever an account is selected, so the
  // "Disputed Booking" summary card is populated in both the transactions and
  // disputes views.
  useEffect(() => {
    if (selectedAccount) {
      fetchDisputes();
    }
  }, [selectedAccount, fetchDisputes]);

  const handleToggleView = () => {
    setView((prev) => (prev === "transactions" ? "disputes" : "transactions"));
    setDisputesPage(1);
    setDisputeSearchQuery("");
    setDisputeStatus("");
    setDisputeDateRange({ submittedAtGt: null, submittedAtLt: null });
  };

  const handleDisputeSearch = (query) => {
    setDisputeSearchQuery(query);
    setDisputesPage(1); // Reset to first page on search
  };

  const handleDisputeStatusChange = (status) => {
    setDisputeStatus(status || "");
    setDisputesPage(1); // Reset to first page on filter change
  };

  const handleDisputeDateRangeApply = (start, end) => {
    setDisputeDateRange({ submittedAtGt: start, submittedAtLt: end });
    setDisputesPage(1); // Reset to first page on filter change
  };

  const handleDisputeDownload = async () => {
    if (!selectedAccount) return;
    try {
      const response = await downloadDisputesCsv({
        currency: selectedAccount.currency,
        status: disputeStatus || undefined,
        query: debouncedDisputeSearch || undefined,
        submittedAtGt: disputeDateRange.submittedAtGt || undefined,
        submittedAtLt: disputeDateRange.submittedAtLt || undefined,
      });
      if (response && response.data) {
        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const timestamp = new Date().toISOString().split("T")[0];
        link.download = `disputes_${selectedAccount.currency}_${timestamp}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      if (error?.message !== "SESSION_EXPIRED") {
        toast.error("Failed to download disputes. Please try again.");
      }
    }
  };

  const handleOpenDisputeForm = async (booking) => {
    if (!booking) return;
    // A room can have several ledger rows (e.g. a booking then a cancel/refund),
    // each with its own amounts. The dispute must use the LATEST record, so fetch
    // the newest row for this room (the list returns newest-first) instead of
    // trusting whichever row the user clicked — otherwise the preview can show a
    // stale/higher amount than what's actually disputable.
    try {
      const response = await getBookingStatus({
        currency: selectedAccount?.currency,
        query: booking.ht_confirmation_id,
        page: 1,
        size: 1,
      });
      const latest = response?.data?.data?.[0];
      // The search is a LIKE, so guard against substring collisions (e.g. the
      // query "…-1" matching "…-10"): only adopt the fetched row when it's the
      // same room; otherwise fall back to the clicked row (right room, and the
      // backend still enforces the correct amount on submit).
      setDisputeFormBooking(
        latest && latest.ht_confirmation_id === booking.ht_confirmation_id
          ? latest
          : booking
      );
    } catch (error) {
      if (error?.message !== "SESSION_EXPIRED") {
        setDisputeFormBooking(booking);
      }
    }
  };

  const handleDisputeSubmitted = () => {
    // New dispute appears in the disputes table and blocks re-disputing the booking.
    fetchOpenDisputeIds();
    if (view === "disputes") {
      fetchDisputes();
    }
  };

  const handleReviewDispute = (dispute) => {
    setReviewDisputeId(dispute.id);
  };

  // A booking that already has an open dispute can't be re-raised — instead
  // resolve that dispute's id from its room code and open it for viewing.
  const handleViewDisputeForBooking = async (booking) => {
    if (!selectedAccount || !booking) return;
    try {
      const response = await findOpenDisputeByRoomCode({
        currency: selectedAccount.currency,
        htRoomConfirmationCode: booking.ht_confirmation_id,
      });
      if (response?.data?.id) {
        setReviewDisputeId(response.data.id);
      } else {
        toast.error("Could not find the existing dispute for this booking.");
      }
    } catch (error) {
      if (error?.message !== "SESSION_EXPIRED") {
        toast.error("Failed to open the existing dispute. Please try again.");
      }
    }
  };

  const handleDisputeChanged = () => {
    fetchDisputes();
    fetchOpenDisputeIds();
  };

  const handleDisputesPageChange = (page) => setDisputesPage(page);
  const handleDisputesPrevious = () => {
    if (disputesPage > 1) setDisputesPage(disputesPage - 1);
  };
  const handleDisputesNext = () => {
    if (disputesPage < disputesTotalPages) setDisputesPage(disputesPage + 1);
  };

  return (
    <div className="wallet-dashboard-container">
      <div className="wallet-dashboard-content">
        <div className="wallet-dashboard-header">
          <div className="wallet-dashboard-title-section">
            <h1>Wallet Dashboard</h1>
            <h2>A New Hotel Trader Payment Experience</h2>
            <p>
              Secure multi-currency wallet solution for seamless hotel booking
              operations
            </p>
          </div>
          <div className="wallet-dashboard-balance-card">
            <BalanceCard
              balance={totalBalance}
              currency={balances[0]?.currency || "USD"}
            />
          </div>
        </div>
        <AccountsTable
          accounts={balances}
          onAccountSelect={handleAccountSelect}
          selectedAccountIndex={selectedAccountIndex}
          onThresholdUpdate={handleThresholdUpdate}
        />
        {selectedAccount && (
          <>
            <SummaryCards
              availableBalance={selectedAccount?.available_amount}
              currency={selectedAccount?.currency}
              threshold={selectedAccount?.threshold}
              dueBooking={selectedAccount?.due_7_days}
              disputedBookingAmount={disputeAggregates.disputedBookingAmount}
              feeImpact={disputeAggregates.feeImpact}
            />

            {view === "transactions" ? (
              <>
                <BookingsTableFilters
                  onSearch={handleSearch}
                  onDownload={handleDownload}
                  onDateRangeApply={handleDateRangeApply}
                  onFilterChange={handleFilterChange}
                  searchValue={searchQuery}
                  onViewToggle={handleToggleView}
                  viewToggleLabel="View Disputes"
                />
                {loadingBookings && isInitialLoad ? (
                  <div style={{ padding: "20px", textAlign: "center" }}>
                    Loading bookings...
                  </div>
                ) : bookingsError ? (
                  <div
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "red",
                    }}
                  >
                    Error: {bookingsError}
                  </div>
                ) : (
                  <div style={{ position: "relative" }}>
                    {loadingBookings && !isInitialLoad && (
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: "rgba(255, 255, 255, 0.7)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 10,
                          borderRadius: "8px",
                        }}
                      >
                        <div style={{ textAlign: "center" }}>
                          <div
                            style={{
                              border: "3px solid #f3f3f3",
                              borderTop: "3px solid #1976d2",
                              borderRadius: "50%",
                              width: "40px",
                              height: "40px",
                              animation: "spin 1s linear infinite",
                              margin: "0 auto",
                            }}
                          ></div>
                        </div>
                      </div>
                    )}
                    <BookingsTable
                      bookings={bookings}
                      selectedAccount={selectedAccount}
                      onDispute={handleOpenDisputeForm}
                      onViewDispute={handleViewDisputeForBooking}
                      openDisputeIds={openDisputeIds}
                    />
                  </div>
                )}
                {total > 10 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    total={total}
                    onPageChange={handlePageChange}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                  />
                )}
              </>
            ) : (
              <>
                <DisputesTableFilters
                  onSearch={handleDisputeSearch}
                  searchValue={disputeSearchQuery}
                  onViewToggle={handleToggleView}
                  statusValue={disputeStatus}
                  onStatusChange={handleDisputeStatusChange}
                  onDateRangeApply={handleDisputeDateRangeApply}
                  onDownload={handleDisputeDownload}
                />
                {loadingDisputes ? (
                  <div style={{ padding: "20px", textAlign: "center" }}>
                    Loading disputes...
                  </div>
                ) : (
                  <DisputesTable
                    disputes={disputes}
                    currency={selectedAccount.currency}
                    onReview={handleReviewDispute}
                  />
                )}
                {disputesTotal > 10 && (
                  <Pagination
                    currentPage={disputesPage}
                    totalPages={disputesTotalPages}
                    total={disputesTotal}
                    onPageChange={handleDisputesPageChange}
                    onPrevious={handleDisputesPrevious}
                    onNext={handleDisputesNext}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>

      <DisputeFormModal
        isOpen={Boolean(disputeFormBooking)}
        mode="submit"
        htConfirmationId={disputeFormBooking?.ht_confirmation_id}
        invoiceAmount={
          disputeFormBooking?.transacted_booking_amount ??
          disputeFormBooking?.ht_booking_amount
        }
        transactedPlatformFee={disputeFormBooking?.transacted_platform_fee}
        currency={selectedAccount?.currency}
        bookingDetails={
          disputeFormBooking
            ? {
                propertyName: disputeFormBooking.property_name,
                clientConfirmationId:
                  disputeFormBooking.client_room_confirmation_id,
                bookingType: disputeFormBooking.booking_type,
                bookingDate: disputeFormBooking.booking_date,
                guestName: disputeFormBooking.guest_name,
                checkInDate: disputeFormBooking.check_in_date,
                checkOutDate: disputeFormBooking.check_out_date,
              }
            : null
        }
        onClose={() => setDisputeFormBooking(null)}
        onSubmitted={handleDisputeSubmitted}
      />

      <DisputeDetailModal
        isOpen={Boolean(reviewDisputeId)}
        disputeId={reviewDisputeId}
        onClose={() => setReviewDisputeId(null)}
        onChanged={handleDisputeChanged}
      />
    </div>
  );
};

WalletDashboard.propTypes = {
  balances: PropTypes.arrayOf(
    PropTypes.shape({
      currency: PropTypes.string.isRequired,
      available_amount: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      usd_total_amount: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      threshold: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      due_7_days: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  onRefreshData: PropTypes.func,
};

export default WalletDashboard;
